// Copyright 2022, Imprivata, Inc.  All rights reserved.

import { encodeObjectProperties } from '@imprivata-cloud/common';
import { AuthnMethod } from '@imprivata-cloud/authn';
import type { AuthenticationStatusCode } from '../../types';
import type { MemorySection } from './MemorySection';
import { AgentEvent, type InternetConnectivityStatus } from '../../constants';
import { type SessionData, ShowWindowOption } from '../../agent-api/types';
import { ProxReader } from '../devices';
import {
  TEST_SAML_STRING,
  agentData,
  deviceData,
  systemData,
} from '../staticData';
import { log } from '../utils';
import { mockWorkflowId } from '../index';
import { getTraceContext } from '../../tracing/traceContext';

export class ConfigSection {
  constructor(private readonly _memory: MemorySection) {}
  simulateConnectDevice(deviceType: AuthnMethod): void {
    switch (deviceType) {
      case AuthnMethod.PROX:
        this._memory.connectedDevices.set(AuthnMethod.PROX, new ProxReader());
        break;
      default:
        throw new Error(`Unsupported deviceType: ${deviceType}`);
    }

    const eventName = AgentEvent.FACTOR_CHANGED;
    const callback = this._memory.registeredCallbacks[eventName];

    log('simulateConnectDevice called.', deviceType, eventName);
    const traceContext = getTraceContext();
    if (callback) {
      const factorList = Array.from(this._memory.connectedDevices.keys());
      callback(eventName, {
        traceContext: traceContext,
        factorList,
      });
    }
  }

  simulateDisconnectDevice(deviceType: AuthnMethod) {
    if (this._memory.connectedDevices.has(deviceType)) {
      this._memory.connectedDevices.delete(deviceType);

      const eventName = AgentEvent.FACTOR_CHANGED;
      const callback = this._memory.registeredCallbacks[eventName];

      log('simulateDisconnectDevice called.', eventName);

      if (callback) {
        const factorList = Array.from(this._memory.connectedDevices.keys());
        const traceContext = getTraceContext();
        callback(eventName, {
          traceContext: traceContext,
          factorList,
        });
      }
    }
  }

  simulateDeviceInteraction(
    deviceType: AuthnMethod,
    eventType: AgentEvent,
    options: Record<string, string>,
  ) {
    const device = this._memory.connectedDevices.get(deviceType);

    if (device) {
      const [traceContext, deviceResult] = device.simulateEvent(
        eventType,
        options || {},
      );

      if (eventType === AgentEvent.FACTOR_DATA_READY) {
        const callback = this._memory.registeredCallbacks[eventType];
        if (callback) {
          const authnData = {
            ...encodeObjectProperties({
              agentData,
              deviceData,
              systemData,
              ...deviceResult,
            }),
            samlData: TEST_SAML_STRING,
            factorType: deviceType,
          };
          callback(eventType, { traceContext, authnData });
        }
      }
    }
  }

  simulateCloseWindow(): void {
    const eventName = AgentEvent.CLOSE_WINDOW;
    const callback = this._memory.registeredCallbacks[eventName];
    if (callback) {
      const traceContext = getTraceContext();
      callback(eventName, { traceContext: traceContext });
    }
  }

  sendAuthenticationStatus(statusCode: AuthenticationStatusCode) {
    const eventName = AgentEvent.APP_AUTHENTICATION_STATUS;
    const callback = this._memory.registeredCallbacks[eventName];
    if (callback) {
      const traceContext = getTraceContext();
      callback(eventName, {
        traceContext: traceContext,
        statusData: { statusCode },
      });
    }
  }

  sendNetworkConnectivityStatus(statusCode: InternetConnectivityStatus) {
    const eventName = AgentEvent.NETWORK_CONNECTIVITY_CHANGED;
    const callback = this._memory.registeredCallbacks[eventName];
    if (callback) {
      const traceContext = getTraceContext();
      callback(eventName, {
        traceContext: traceContext,
        event: statusCode,
      });
    }
  }

  setAppAccessContext(appAccessContext: string) {
    this._memory.appAccessContext = appAccessContext;
  }

  setIdentityToken(identityToken: string) {
    this._memory.identityToken = identityToken;
  }

  setCodingContext(codingContext: string) {
    this._memory.codingContext = codingContext;
  }

  setSystemData(systemData: string) {
    this._memory.systemData = systemData;
  }

  setSessionData(sessionData: SessionData) {
    this._memory.sessionData = sessionData;
  }

  getConnectedDevices(): AuthnMethod[] {
    return Array.from(this._memory.connectedDevices.keys());
  }

  getSubmittedCredentials(): CredentialData[] {
    return this._memory.submittedCredentials;
  }

  getSubmittedOidcAuthCodes(): string[] {
    return this._memory.submittedOidcAuthCodes;
  }

  onShowWindowChange(
    callback: (showWindowOption: ShowWindowOption) => void,
  ): void {
    log('onShowWindowChange binded');
    this._memory.onShowWindowChangeCallback = callback;
  }

  openUi({
    contextType,
    resourceType,
    tenantId,
  }: {
    contextType: string;
    resourceType: string;
    tenantId: string;
  }) {
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.set('contextType', contextType);
    queryParams.set('resourceType', resourceType);
    queryParams.set('tenantId', tenantId);

    sessionStorage.setItem('tu__journey_id', mockWorkflowId);
    queryParams.set('journeyId', mockWorkflowId);

    window.location.search = queryParams.toString();
    this._memory.onShowWindowChangeCallback?.(ShowWindowOption.show);
  }
}
